'use client'
import type { HeroBlock as HeroBlockProps } from 'src/payload-types'
import { cn } from '@/utilities/ui'
import React, { use, useEffect, useRef, useState } from 'react'
import RichText from '@/components/RichText'
import { Search, Home, SlidersHorizontal, MapPin, HomeIcon } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import LoginModal from './LoginModal'
import { useTheme } from '@/providers/Theme'
import AdvancedSearchModal from './AdvancedSearchModal'
import { useRouter } from 'next/navigation'
import { Media } from '@/components/Media'

type Props = {
  className?: string
  lang?: string
  dictionary?: any
} & HeroBlockProps

export const HeroBlock = ({
  className,
  content,
  backgroundImage,
  buttonText = 'Search',
  tabOptions = [],
  initialTab = 'buy',
  lang,
  dictionary,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<any>(null)
  const router = useRouter()
  const [isMobile, setIsMobile] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState(initialTab || 'buy')
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState({})
  const { theme } = useTheme()
  //listedIn=for_rent
  const [listedIn, setListedIn] = useState('for_sale')

  // Check if RTL
  const isRTL = lang === 'ar'

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Add language to the URL if available
    const baseUrl = lang ? `/${lang}/properties` : `/properties`
    router.push(`${baseUrl}?search=${searchQuery}&listedIn=${listedIn}`)
  }

  const handleAdvancedSearch = (filters) => {
    setAdvancedFilters(filters)
    console.log('Advanced filters applied:', filters)
    // You can handle search with filters here or combine with the main search
  }

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)

    return () => {
      window.removeEventListener('resize', checkIsMobile)
    }
  }, [])

  useOnClickOutside(menuRef, () => {
    if (isOpen) {
      setIsOpen(false)
    }
  })

  return (
    <div className={cn('relative w-full sm:pt-14 bg-light ', className)}>
      {/* Background Image */}
      {backgroundImage && (
        <Media
          resource={backgroundImage}
          className=" "
          imgClassName="w-full h-full absolute object-fill inset-0 z-0"
          alt={'bg image'}
          loading="eager"
          priority
        />
      )}

      <div className="w-full h-full absolute inset-0 z-[1] bg-black opacity-30"></div>

      {/* Content */}
      <div className="relative z-[11] mx-auto max-w-screen-xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className={cn('flex flex-col items-center text-center', isRTL && 'text-right')}>
          {/* Rich Text Content */}
          {content && (
            <div className="sm:my-28 my-14 max-w-4xl not-prose">
              <RichText data={content} className="text-white" />
            </div>
          )}

          {/* Search Form */}
          <div className="sm:w-[80%] w-[95%] rounded-lg p-1">
            {/* Tabs */}
            <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className={cn('w-full flex', isRTL ? 'justify-end' : 'justify-start')}>
                <TabsList
                  className={cn('grid bg-white w-[165px] grid-cols-2', isRTL && 'flex-row-reverse')}
                >
                  {tabOptions.length > 0 ? (
                    tabOptions.map((tab) => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className={cn(
                          'data-[state=active]:border-b data-[state=active]:border-black data-[state=active]:text-black',
                          'data-[state=inactive]:text-gray-500',
                        )}
                        onClick={(e) => {
                          console.log('clicked for rent ', e)
                          setListedIn('for_rent')
                        }}
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))
                  ) : (
                    <>
                      <TabsTrigger
                        value="buy"
                        className={cn(
                          'data-[state=active]:border-b data-[state=active]:border-black data-[state=active]:text-black',
                          'data-[state=inactive]:text-gray-500',
                        )}
                        onClick={() => setListedIn('for_sale')}
                      >
                        {dictionary?.propertyListing?.asideFilter?.buy || 'Buy'}
                      </TabsTrigger>
                      <TabsTrigger
                        value="rent"
                        className={cn(
                          'data-[state=active]:border-b rounded-none data-[state=active]:border-black data-[state=active]:text-black',
                          'data-[state=inactive]:text-gray-500',
                        )}
                        onClick={() => {
                          console.log('clicked for rent ')
                          setListedIn('for_rent')
                        }}
                      >
                        {dictionary?.propertyListing?.asideFilter?.rent || 'Rent'}
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>
              </div>

              {/* Search Form - Same for all tabs */}
              {(tabOptions.length > 0
                ? tabOptions
                : [
                    { value: 'buy', filtre: 'for_sell' },
                    { value: 'rent', filtre: 'for_rent' },
                  ]
              ).map((tab) => (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className={cn(
                    'mt-0 p-5 bg-white',
                    isRTL ? 'rounded-b-lg rounded-l-lg' : 'rounded-b-lg rounded-r-lg',
                  )}
                >
                  <form
                    onSubmit={handleSearch}
                    className={cn(
                      'flex flex-col items-center md:flex-row gap-4',
                      isRTL && 'md:flex-row-reverse',
                    )}
                  >
                    <div className="flex-1 max-md:w-full">
                      <div
                        ref={menuRef}
                        className={cn(
                          'relative flex w-full items-center rounded-md border bg-[rgba(247,247,247,1)] px-3 py-1',
                          isRTL && 'flex-row-reverse',
                        )}
                      >
                        <Home className={cn('h-5 w-5 text-gray-500', isRTL ? 'ml-2' : 'mr-2')} />
                        <Input
                          type="text"
                          placeholder={
                            dictionary?.propertyListing?.asideFilter?.whatAreYouLookingFor ||
                            'Enter an address, neighborhood, city, or ZIP code'
                          }
                          className={cn(
                            'w-full border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-black',
                            isRTL && 'text-right',
                          )}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                    <div
                      className={cn(
                        'max-md:w-full flex items-center flex-row gap-4',
                        isRTL && 'flex-row-reverse',
                      )}
                    >
                      {/* Advanced Button - Now opens the modal */}
                      <div className="flex-1">
                        <Button
                          type="button"
                          variant={isMobile ? 'light' : 'ghost'}
                          className={cn(
                            'bg-white flex w-full h-[45px] items-center justify-center hover:bg-gray-200 md:w-auto',
                            isRTL && 'flex-row-reverse',
                          )}
                          onClick={() => setIsAdvancedModalOpen(true)}
                        >
                          <SlidersHorizontal className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')} />
                          {dictionary?.propertyListing?.filter || buttonText}
                        </Button>
                      </div>

                      {/* Search Button */}
                      <div className="max-md:flex-1">
                        <Button
                          type="submit"
                          className={cn(
                            'flex md:size-14 max-md:w-full max-md:h-[45px] md:rounded-full rounded-xl items-center justify-center gap-2 bg-[rgba(0,102,51,1)] hover:bg-[rgba(0,82,41,1)] text-white',
                            isRTL && 'flex-row-reverse',
                          )}
                        >
                          <Search className="size-6" />
                          {isMobile &&
                            (dictionary?.propertyListing?.asideFilter?.search || 'search')}
                        </Button>
                      </div>
                    </div>
                  </form>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>

      {/* Advanced Search Modal */}
      <AdvancedSearchModal
        isOpen={isAdvancedModalOpen}
        onClose={() => setIsAdvancedModalOpen(false)}
        title={dictionary?.advancedSearch?.title || 'Advanced Search'}
        searchText={searchQuery}
        listedIn={listedIn}
        dictionary={dictionary}
        lang={lang}
      />

      {/* Login Modal */}
      <LoginModal open={isLoginModalOpen} onOpenChange={setIsLoginModalOpen} />
    </div>
  )
}
